import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { NAV, isGroup, isLabel, type NavGroup, type NavItem } from '../../lib/nav'
import { cn } from '../../lib/cn'

// Brand theme: black + #E1021B
const SIDEBAR_BG = 'linear-gradient(173deg,#161618 0%,#0c0c0e 58%,#070708 100%)'

function Mark({ className }: { className?: string }) {
  return <img src="/brand/logo-w.png" alt="Garage San Marco Venezia" className={className} />
}

function Leaf({ item, sub, collapsed }: { item: NavItem; sub?: boolean; collapsed?: boolean }) {
  return (
    <NavLink
      to={item.to}
      end
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex w-full items-center rounded-[9px] transition-colors',
          collapsed ? 'justify-center px-0 py-[10px]' : sub ? 'gap-[11px] py-[7px] pl-3 pr-3 text-[13.5px]' : 'gap-[11px] px-3 py-[9px] text-[14px]',
          isActive
            ? 'bg-[#E1021B] font-semibold text-white shadow-[0_4px_14px_-6px_rgba(225,2,27,.8)]'
            : cn('font-medium', sub ? 'text-[#9a9aa1]' : 'text-[#b6b6bc]', 'hover:bg-white/[0.06] hover:text-white'),
        )
      }
    >
      {({ isActive }) => (
        <>
          {item.icon && (
            <item.icon
              className={cn('h-[18px] w-[18px] shrink-0', isActive ? 'text-white opacity-100' : 'opacity-80')}
              strokeWidth={1.8}
            />
          )}
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  )
}

function Group({
  group,
  open,
  onToggle,
  collapsed,
  onExpandAndOpen,
}: {
  group: NavGroup
  open: boolean
  onToggle: () => void
  collapsed?: boolean
  onExpandAndOpen: () => void
}) {
  const { pathname } = useLocation()
  const containsActive = group.items.some((i) => pathname === i.to || pathname.startsWith(i.to + '/'))
  const Icon = group.icon

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onExpandAndOpen}
        title={group.label}
        className={cn(
          'flex w-full items-center justify-center rounded-[9px] py-[10px] transition-colors',
          containsActive ? 'text-[#E1021B]' : 'text-[#b6b6bc] hover:bg-white/[0.06] hover:text-white',
        )}
      >
        <Icon className="h-[18px] w-[18px] opacity-90" strokeWidth={1.8} />
      </button>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          'group flex w-full items-center gap-[11px] rounded-[9px] px-3 py-[9px] text-[14px] font-medium transition-colors',
          open || containsActive ? 'text-white' : 'text-[#b6b6bc] hover:bg-white/[0.06] hover:text-white',
        )}
      >
        <Icon
          className={cn('h-[18px] w-[18px] shrink-0', containsActive ? 'text-[#E1021B] opacity-100' : 'opacity-80')}
          strokeWidth={1.8}
        />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 opacity-55 transition-transform duration-200', open && 'rotate-90')}
          strokeWidth={2.4}
        />
      </button>
      {open && (
        <div className="my-0.5 mb-1.5 ml-[21px] border-l border-white/[0.08] pl-[18px]">
          {group.items.map((it) => (
            <Leaf key={it.to} item={it} sub />
          ))}
        </div>
      )}
    </div>
  )
}

export function Sidebar() {
  const { pathname } = useLocation()

  const activeGroup = useMemo(() => {
    for (const e of NAV) {
      if (isGroup(e) && e.items.some((i) => pathname === i.to || pathname.startsWith(i.to + '/'))) {
        return e.label
      }
    }
    return null
  }, [pathname])

  const [open, setOpen] = useState<string | null>(activeGroup)
  useEffect(() => {
    if (activeGroup) setOpen(activeGroup)
  }, [activeGroup])

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('gsm_sidebar_collapsed') === '1'
    } catch {
      return false
    }
  })
  function setCollapsedPersist(v: boolean) {
    setCollapsed(v)
    try {
      localStorage.setItem('gsm_sidebar_collapsed', v ? '1' : '0')
    } catch {
      /* ignore */
    }
  }

  return (
    <aside
      className={cn(
        'flex h-dvh shrink-0 flex-col overflow-y-auto overflow-x-hidden text-[#d9d9de] transition-[width] duration-200',
        collapsed ? 'w-[68px]' : 'w-[268px]',
      )}
      style={{ background: SIDEBAR_BG }}
    >
      {/* brand */}
      <div
        className={cn(
          'flex items-center border-b border-white/[0.07] pb-[18px] pt-[22px]',
          collapsed ? 'justify-center px-0' : 'gap-3 px-6',
        )}
      >
        <Mark className={collapsed ? 'h-[22px] w-auto shrink-0' : 'h-[26px] w-auto shrink-0'} />
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-display text-[16px] font-bold tracking-tight text-white">Garage San Marco</p>
            <p className="mt-[2px] text-[10.5px] font-semibold uppercase tracking-[1.6px] text-[#8a8a90]">
              Gestionale · Venezia
            </p>
          </div>
        )}
      </div>

      <nav className={cn('flex-1 space-y-0.5 py-3', collapsed ? 'px-2' : 'px-[14px]')}>
        {NAV.map((entry, i) => {
          if (isLabel(entry)) {
            return collapsed ? (
              <div key={`l${i}`} className="my-2 h-px bg-white/[0.07]" />
            ) : (
              <div key={`l${i}`} className="px-3 pb-[7px] pt-[18px] text-[10px] font-bold uppercase tracking-[2.2px] text-[#6a6a71]">
                {entry.sectionLabel}
              </div>
            )
          }
          if (isGroup(entry)) {
            return (
              <Group
                key={entry.label}
                group={entry}
                collapsed={collapsed}
                open={open === entry.label}
                onToggle={() => setOpen((o) => (o === entry.label ? null : entry.label))}
                onExpandAndOpen={() => {
                  setCollapsedPersist(false)
                  setOpen(entry.label)
                }}
              />
            )
          }
          return <Leaf key={entry.to} item={entry} collapsed={collapsed} />
        })}
      </nav>

      <div className={cn('border-t border-white/[0.07]', collapsed ? 'px-2 py-3' : 'px-4 py-3')}>
        <button
          type="button"
          onClick={() => setCollapsedPersist(!collapsed)}
          title={collapsed ? 'Espandi menu' : 'Comprimi menu'}
          className={cn(
            'flex items-center rounded-[8px] text-[12.5px] font-medium text-[#8a8a90] transition-colors hover:bg-white/[0.06] hover:text-white',
            collapsed ? 'w-full justify-center py-2' : 'w-full gap-2.5 px-2.5 py-2',
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.8} />
          ) : (
            <>
              <PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={1.8} />
              <span>Comprimi menu</span>
            </>
          )}
        </button>
        {!collapsed && (
          <div className="mt-2.5 px-2 text-[11.5px] leading-relaxed text-[#6a6a71]">
            <b className="font-semibold text-[#c6c6cb]">booking.garagesanmarco.it</b>
            <br />
            v0.1 · React rebuild
          </div>
        )}
      </div>
    </aside>
  )
}
